import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X, Upload, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'industriel', label: 'Industriel' },
  { value: 'residentiel', label: 'Résidentiel' },
  { value: 'infrastructure', label: 'Infrastructure' },
];

const AdminRealisations = () => {
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedRealisation, setSelectedRealisation] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    year: new Date().getFullYear(),
    category: 'commercial',
    is_featured: false
  });
  const [uploading, setUploading] = useState(false);

  const fetchRealisations = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/realisations`, { withCredentials: true });
      setRealisations(data);
    } catch (e) {
      toast.error('Erreur lors du chargement des réalisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRealisations();
  }, []);

  const handleEdit = (realisation) => {
    setSelectedRealisation(realisation);
    setFormData({
      title: realisation.title,
      description: realisation.description,
      location: realisation.location || '',
      year: realisation.year || new Date().getFullYear(),
      category: realisation.category || 'commercial',
      is_featured: realisation.is_featured || false
    });
    setEditDialog(true);
  };

  const handleCreate = () => {
    setSelectedRealisation(null);
    setFormData({
      title: '',
      description: '',
      location: '',
      year: new Date().getFullYear(),
      category: 'commercial',
      is_featured: false
    });
    setEditDialog(true);
  };

  const handleDelete = (realisation) => {
    setSelectedRealisation(realisation);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/admin/realisations/${selectedRealisation.id}`, { withCredentials: true });
      toast.success('Réalisation supprimée');
      fetchRealisations();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialog(false);
      setSelectedRealisation(null);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedRealisation) {
        await axios.put(`${API}/api/admin/realisations/${selectedRealisation.id}`, formData, { withCredentials: true });
        toast.success('Réalisation mise à jour');
      } else {
        await axios.post(`${API}/api/admin/realisations`, formData, { withCredentials: true });
        toast.success('Réalisation créée');
      }
      fetchRealisations();
      setEditDialog(false);
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (selectedRealisation) {
        await axios.put(`${API}/api/admin/realisations/${selectedRealisation.id}`, 
          { image_url: `${API}${data.url}` }, 
          { withCredentials: true }
        );
        toast.success('Image uploadée');
        fetchRealisations();
      }
    } catch (e) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white h-24 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-realisations">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-1">Réalisations</h1>
          <p className="text-slate-600">Gérez le portfolio de projets</p>
        </div>
        <Button onClick={handleCreate} className="bg-navy hover:bg-navy/90 text-white rounded-sm" data-testid="create-realisation-btn">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter une réalisation
        </Button>
      </div>

      {/* Realisations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {realisations.map((realisation) => (
          <div key={realisation.id} className="bg-white border border-slate-200 overflow-hidden group" data-testid={`realisation-row-${realisation.id}`}>
            <div className="aspect-video bg-slate-100 relative">
              {realisation.image_url ? (
                <img src={realisation.image_url} alt={realisation.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                  Pas d'image
                </div>
              )}
              {realisation.is_featured && (
                <div className="absolute top-2 left-2 bg-rose-600 text-white text-xs font-bold px-2 py-1 flex items-center gap-1">
                  <Star className="h-3 w-3" /> À la une
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-cyan-600 text-xs font-bold uppercase">{realisation.category}</span>
                {realisation.year && <span className="text-slate-400 text-xs">• {realisation.year}</span>}
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-1">{realisation.title}</h3>
              <p className="text-slate-600 text-sm line-clamp-2">{realisation.description}</p>
              <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(realisation)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(realisation)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {realisations.length === 0 && (
          <div className="col-span-full bg-white border border-slate-200 p-12 text-center text-slate-500">
            Aucune réalisation. Cliquez sur "Ajouter une réalisation" pour commencer.
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedRealisation ? 'Modifier la réalisation' : 'Nouvelle réalisation'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Localisation</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Paris, Île-de-France"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Année</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Catégorie</Label>
              <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm">
              <div>
                <p className="font-medium text-slate-900">Mettre à la une</p>
                <p className="text-sm text-slate-600">Afficher cette réalisation en vedette sur la page d'accueil</p>
              </div>
              <Switch 
                checked={formData.is_featured} 
                onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })} 
              />
            </div>

            {selectedRealisation && (
              <div>
                <Label>Image</Label>
                <div className="mt-1">
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 cursor-pointer hover:border-cyan-500 transition-colors">
                    <Upload className="h-5 w-5 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      {uploading ? 'Upload...' : 'Choisir une image'}
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                <X className="h-4 w-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-navy hover:bg-navy/90 text-white">
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette réalisation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La réalisation "{selectedRealisation?.title}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminRealisations;
